import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate } from 'react-router-dom'
import { cn } from '../../lib/cn.js'
import { standingBadge, standingDetail } from '../../lib/licences.js'
import { platform } from '../../lib/native.js'
import { useConnectionState } from '../../lib/realtime.js'
import { useSession } from '../../lib/session.js'
import { errorMessage, setBearerToken, useTRPC } from '../../lib/trpc.js'
import { Icon, type IconName } from '../Icon.js'
import { NAV } from '../nav.js'
import { Avatar, Badge, SkeletonRows } from '../ui.js'
import { useAppVersion } from '../../lib/app-version.js'
import { FieldScreen, Group, ScreenHeader, cardClass, rowClass } from './kit.js'

/** Screens the field app draws as a tab, and so leaves off Me's list. */
const TABS = new Set(['/', '/jobs', '/inbox'])

/**
 * You, and the way out to everything else.
 *
 * The licence card is here because it is the one thing about themselves a
 * technician is asked for on site. Below it, whatever screens this person may
 * open that the field app has no tab for — the board, clients, money,
 * settings — so the office loses nothing by holding an iPad.
 */
export function FieldMe() {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const { user, can } = useSession()
  const connection = useConnectionState()
  const version = useAppVersion()
  const versionLine = version.current ?? version.behind

  const licences = useQuery({
    ...trpc.licences.forUser.queryOptions({ userId: user?.id ?? '' }),
    enabled: Boolean(user),
  })

  const logout = useMutation(
    trpc.auth.logout.mutationOptions({
      onSuccess: () => {
        setBearerToken(null)
        queryClient.clear()
        navigate('/login', { replace: true })
      },
    }),
  )

  const screens: { to: string; label: string; icon: IconName }[] = [
    ...(can('JOB_VIEW_ALL') ? [{ to: '/worklist', label: 'Job worklist', icon: 'jobs' as const }] : []),
    ...NAV.filter(
      (item) => !TABS.has(item.to) && (!item.permission || can(item.permission)),
    ).map(({ to, label, icon }) => ({ to, label, icon })),
  ]

  const endorsements = (licences.data ?? []).filter(
    (entry) => entry.holding || entry.licenceType.expectedOfFieldStaff,
  )

  return (
    <FieldScreen>
      <ScreenHeader title="Me" />

      <div className={cn(cardClass, 'flex items-center gap-3.5 px-4 py-3.5')}>
        <Avatar name={user?.name ?? '?'} colour={user?.colour} className="size-14 text-[18px]" />
        <div className="min-w-0">
          <p className="truncate text-[20px] font-bold text-ink-900">{user?.name}</p>
          <p className="truncate text-[14px] text-ink-500">
            {[user?.roleName, user?.email].filter(Boolean).join(' · ')}
          </p>
        </div>
      </div>

      <Group label="Licence endorsements">
        {licences.isPending ? (
          <SkeletonRows rows={2} />
        ) : endorsements.length === 0 ? (
          <p className="px-4 py-3.5 text-[14px] text-ink-500">No endorsements recorded.</p>
        ) : (
          endorsements.map((entry) => {
            const badge = standingBadge(entry.standing)
            return (
              <div key={entry.licenceType.id} className={rowClass}>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[15px] font-medium text-ink-900">
                    {entry.licenceType.name}
                  </p>
                  <p className="truncate text-[13px] text-ink-500">{standingDetail(entry)}</p>
                </div>
                <Badge tone={badge.tone} className="rounded-md px-2 py-0.5 text-xs">
                  {badge.label}
                </Badge>
              </div>
            )
          })
        )}
      </Group>

      {screens.length > 0 ? (
        <Group label="More">
          {screens.map((screen) => (
            <Link key={screen.to} to={screen.to} className={rowClass}>
              <span className="grid size-8 shrink-0 place-items-center rounded-lg bg-ink-100 text-ink-700">
                <Icon name={screen.icon} className="size-[18px]" />
              </span>
              <span className="min-w-0 flex-1 text-[15px] font-medium text-ink-900">
                {screen.label}
              </span>
              <Icon name="forward" className="size-4 text-ink-400" />
            </Link>
          ))}
        </Group>
      ) : null}

      <Group label="This device">
        <div className={rowClass}>
          <span className="flex-1 text-[15px] text-ink-900">Connection</span>
          <span
            className={cn(
              'text-[14px]',
              connection === 'open' ? 'text-ink-500' : 'font-medium text-amber-800',
            )}
          >
            {connection === 'open' ? 'Live' : connection === 'connecting' ? 'Reconnecting…' : 'Offline'}
          </span>
        </div>
        <div className={rowClass}>
          <span className="flex-1 text-[15px] text-ink-900">Report answers</span>
          <span className="text-[14px] text-ink-500">Kept on this device until sent</span>
        </div>
        {versionLine ? (
          <div className={rowClass}>
            <span className="flex-1 text-[15px] text-ink-900">Version</span>
            <span className="text-[14px] text-ink-500">{versionLine.replace(/^Version /, '')}</span>
          </div>
        ) : null}
        <div className={rowClass}>
          <span className="flex-1 text-[15px] text-ink-900">App</span>
          <span className="text-[14px] text-ink-500">
            {platform() === 'ios' ? 'iOS' : platform() === 'android' ? 'Android' : 'Web'}
          </span>
        </div>
      </Group>

      <div className={cardClass}>
        <button
          type="button"
          disabled={logout.isPending}
          onClick={() => logout.mutate()}
          className={cn(rowClass, 'justify-center text-[16px] font-semibold text-red-600 disabled:opacity-60')}
        >
          {logout.isPending ? 'Signing out…' : 'Sign out'}
        </button>
      </div>
      {logout.error ? (
        <p role="alert" className="px-1 text-[13px] text-red-700">
          {errorMessage(logout.error)}
        </p>
      ) : null}
    </FieldScreen>
  )
}
